import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Row,
  Col,
  message,
  Typography,
  Modal,
  Form,
  Input,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { centersAPI } from '../../utils/api';
import moment from 'moment';

const { Title } = Typography;

const Centers = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [adminModalVisible, setAdminModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [form] = Form.useForm();
  const [adminForm] = Form.useForm();

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      const response = await centersAPI.getCenters();
      setCenters(response.data.centers || []);
    } catch (error) {
      message.error('Failed to fetch wellness centers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await centersAPI.updateCenter(editingRecord.id, values);
        message.success('Wellness center updated successfully');
      } else {
        await centersAPI.createCenter(values);
        message.success('Wellness center created successfully');
      }
      
      setModalVisible(false);
      setEditingRecord(null);
      form.resetFields();
      fetchCenters();
    } catch (error) {
      message.error(`Failed to ${editingRecord ? 'update' : 'create'} wellness center`);
    }
  };

  const handleCreateAdmin = async (values) => {
    try {
      const response = await centersAPI.createCenterAdmin(selectedCenter.id, values);
      const adminData = response.data;
      
      // Show success message with password
      Modal.success({
        title: 'Center Admin Created Successfully!',
        width: 500,
        content: (
          <div>
            <p><strong>Admin Details:</strong></p>
            <p><strong>Name:</strong> {adminData.admin.name}</p>
            <p><strong>Email:</strong> {adminData.admin.email}</p>
            <p><strong>Center:</strong> {adminData.admin.center_name}</p>
            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              backgroundColor: '#f0f0f0', 
              borderRadius: 4,
              border: '1px solid #d9d9d9'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#ff4d4f' }}>
                ⚠️ IMPORTANT: Save this password!
              </p>
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '16px', 
                fontFamily: 'monospace',
                fontWeight: 'bold',
                color: '#1890ff'
              }}>
                Password: {adminData.password}
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                Share these credentials with the center admin. They can change their password after first login.
              </p>
            </div>
          </div>
        ),
        okText: 'Got it',
        onOk: () => {
      setAdminModalVisible(false);
      setSelectedCenter(null);
      adminForm.resetFields();
        }
      });
    } catch (error) {
      message.error('Failed to create center admin');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Wellness Center',
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone and will affect all associated consumers.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await centersAPI.deleteCenter(record.id);
          message.success('Wellness center deleted successfully');
          fetchCenters();
        } catch (error) {
          message.error('Failed to delete wellness center');
        }
      }
    });
  };

  const showAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showAdminModal = (center) => {
    setSelectedCenter(center);
    adminForm.resetFields();
    setAdminModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (address) => address || '-',
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone || '-'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.email || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'Consumers',
      dataIndex: 'consumer_count',
      key: 'consumer_count',
      render: (count) => count || 0,
    },
    {
      title: 'Admins',
      dataIndex: 'admin_count',
      key: 'admin_count',
      render: (count) => count || 0,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            size="small" 
            icon={<UserAddOutlined />}
            onClick={() => showAdminModal(record)}
            title="Add Admin"
          />
          <Button 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Button 
            size="small" 
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record)}
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Wellness Centers
          </Title>
        </Col>
        <Col>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchCenters}
              loading={loading}
            >
              Refresh
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={showAddModal}
            >
              Add Center
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={centers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Add/Edit Center Modal */}
      <Modal
        title={editingRecord ? 'Edit Wellness Center' : 'Add Wellness Center'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Center Name"
            rules={[
              { required: true, message: 'Please enter center name' },
              { min: 2, message: 'Name must be at least 2 characters' }
            ]}
          >
            <Input placeholder="Enter wellness center name" />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input.TextArea 
              rows={3} 
              placeholder="Enter center address"
            />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name="phone" label="Phone Number">
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { type: 'email', message: 'Please enter valid email address' }
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingRecord ? 'Update' : 'Create'} Center
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Admin Modal */}
      <Modal
        title={`Add Admin for ${selectedCenter?.name}`}
        open={adminModalVisible}
        onCancel={() => {
          setAdminModalVisible(false);
          setSelectedCenter(null);
          adminForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={adminForm}
          layout="vertical"
          onFinish={handleCreateAdmin}
        >
          <Form.Item
            name="name"
            label="Admin Name"
            rules={[
              { required: true, message: 'Please enter admin name' },
              { min: 2, message: 'Name must be at least 2 characters' }
            ]}
          >
            <Input placeholder="Enter admin full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter valid email address' }
            ]}
          >
            <Input placeholder="Enter admin email address" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password placeholder="Enter admin password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setAdminModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Admin
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Centers;
